// Opportunity Management Help Content (HTML)
export const opportunityHelpContent = `
<h1>คู่มือการจัดการ Opportunities (Opportunity Management)</h1>

<h2>ภาพรวม</h2>
<p>ระบบจัดการ Opportunities ช่วยให้คุณสามารถติดตามโอกาสการขายตั้งแต่เริ่มต้นจนถึงปิดการขาย พร้อมการจัดการ Pipeline และคาดการณ์รายได้</p>

<h2>Opportunity คืออะไร?</h2>
<p><strong>Opportunity</strong> คือโอกาสในการขายที่เกิดจาก Lead หรือ Customer ที่มีความสนใจในผลิตภัณฑ์หรือบริการของคุณ และอยู่ในขั้นตอนการเจรจาเพื่อปิดการขาย</p>

<h2>Opportunity Stages (ขั้นตอนการขาย)</h2>
<p>Opportunity ผ่านขั้นตอนต่างๆ ตาม Pipeline ที่กำหนดไว้ เช่น:</p>
<ol>
  <li><strong>Prospecting</strong> - สำรวจและคัดกรอง</li>
  <li><strong>Qualification</strong> - ประเมินความเหมาะสม</li>
  <li><strong>Needs Analysis</strong> - วิเคราะห์ความต้องการ</li>
  <li><strong>Proposal/Price Quote</strong> - เสนอข้อเสนอ</li>
  <li><strong>Negotiation/Review</strong> - เจรจาและทบทวน</li>
  <li><strong>Closed Won</strong> - ปิดการขายสำเร็จ</li>
  <li><strong>Closed Lost</strong> - ไม่สามารถปิดการขายได้</li>
</ol>
<p><em>หมายเหตุ: Stages อาจแตกต่างกันตามการตั้งค่า Pipeline ของแต่ละองค์กร</em></p>

<h2>ฟิลด์สำคัญ</h2>

<h3>ฟิลด์พื้นฐาน</h3>
<table>
  <thead>
    <tr>
      <th>ฟิลด์</th>
      <th>คำอธิบาย</th>
      <th>จำเป็น</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>Opportunity Name</strong></td>
      <td>ชื่อของ Opportunity</td>
      <td>✅</td>
    </tr>
    <tr>
      <td><strong>Stage</strong></td>
      <td>ขั้นตอนปัจจุบันใน Pipeline</td>
      <td>✅</td>
    </tr>
    <tr>
      <td><strong>Amount</strong></td>
      <td>มูลค่าโอกาสการขาย (บาท)</td>
      <td>❌</td>
    </tr>
    <tr>
      <td><strong>Probability</strong></td>
      <td>ความน่าจะเป็นในการปิดการขาย (%)</td>
      <td>❌</td>
    </tr>
    <tr>
      <td><strong>Expected Close Date</strong></td>
      <td>วันที่คาดว่าจะปิดการขาย</td>
      <td>❌</td>
    </tr>
    <tr>
      <td><strong>Customer</strong></td>
      <td>ลูกค้าที่เกี่ยวข้อง</td>
      <td>❌</td>
    </tr>
    <tr>
      <td><strong>Lead Source</strong></td>
      <td>แหล่งที่มาของ Opportunity</td>
      <td>❌</td>
    </tr>
    <tr>
      <td><strong>Salesperson</strong></td>
      <td>พนักงานขายที่ดูแล</td>
      <td>❌</td>
    </tr>
  </tbody>
</table>

<h3>ฟิลด์เพิ่มเติม</h3>
<ul>
  <li><strong>Description</strong>: คำอธิบายรายละเอียด</li>
  <li><strong>Next Step</strong>: ขั้นตอนถัดไปที่ต้องทำ</li>
  <li><strong>Competitors</strong>: คู่แข่งในการขาย</li>
  <li><strong>Win/Loss Reason</strong>: เหตุผลในการชนะ/แพ้ (เมื่อปิดแล้ว)</li>
  <li><strong>Actual Close Date</strong>: วันที่ปิดการขายจริง</li>
  <li><strong>Actual Amount</strong>: มูลค่าที่ปิดการขายจริง</li>
</ul>

<h2>การคำนวณ Expected Revenue</h2>
<p>ระบบคำนวณ Expected Revenue (รายได้ที่คาดหวัง) จากสูตร:</p>
<p><strong>Expected Revenue = Amount × (Probability / 100)</strong></p>
<p>ตัวอย่าง: หาก Amount = 100,000 บาท และ Probability = 50%</p>
<p>Expected Revenue = 100,000 × (50/100) = 50,000 บาท</p>

<h2>Pipeline Management</h2>
<p>Pipeline แสดง Opportunities ทั้งหมดในแต่ละ Stage ช่วยให้:</p>
<ul>
  <li>เห็นภาพรวมของโอกาสการขายทั้งหมด</li>
  <li>ระบุ Opportunities ที่ติดค้างในแต่ละ Stage</li>
  <li>คาดการณ์รายได้ (Forecasting)</li>
  <li>วางแผนการติดตาม</li>
</ul>

<h2>การปิด Opportunity</h2>

<h3>Closed Won (ปิดการขายสำเร็จ)</h3>
<ol>
  <li>เปลี่ยน Stage เป็น "Closed Won"</li>
  <li>ระบุ Win Reason</li>
  <li>ระบุ Actual Close Date</li>
  <li>ระบุ Actual Amount (หากแตกต่างจาก Amount)</li>
  <li>บันทึกข้อมูล</li>
</ol>

<h3>Closed Lost (ไม่สามารถปิดการขาย)</h3>
<ol>
  <li>เปลี่ยน Stage เป็น "Closed Lost"</li>
  <li>ระบุ Loss Reason เพื่อเรียนรู้และปรับปรุง</li>
  <li>บันทึกข้อมูล</li>
</ol>

<h2>การเชื่อมโยงข้อมูล</h2>
<p>Opportunity สามารถเชื่อมโยงกับ:</p>
<ul>
  <li><strong>Customer</strong>: ลูกค้าที่เกี่ยวข้อง</li>
  <li><strong>Lead</strong>: Lead ที่แปลงเป็น Opportunity</li>
  <li><strong>Activities</strong>: กิจกรรมที่เกี่ยวข้อง (Meeting, Call, Email)</li>
  <li><strong>Notes</strong>: หมายเหตุและข้อมูลสำคัญ</li>
  <li><strong>Attachments</strong>: เอกสาร (Proposal, Contract, etc.)</li>
  <li><strong>Products</strong>: ผลิตภัณฑ์/บริการที่เสนอขาย</li>
</ul>

<h2>คำแนะนำการใช้งาน</h2>

<h3>การสร้าง Opportunity ใหม่</h3>
<ol>
  <li>กรอกชื่อ Opportunity (จำเป็น)</li>
  <li>เลือก Stage ปัจจุบัน</li>
  <li>ระบุ Amount และ Probability (ถ้ามี)</li>
  <li>ระบุ Expected Close Date</li>
  <li>เลือก Customer หรือ Lead ที่เกี่ยวข้อง</li>
  <li>คลิก "Save" เพื่อบันทึก</li>
</ol>

<h3>การจัดการ Pipeline</h3>
<ul>
  <li>อัปเดต Stage เป็นประจำเมื่อมีความคืบหน้า</li>
  <li>ปรับ Probability ตามความคืบหน้าในการเจรจา</li>
  <li>เพิ่ม Activities เพื่อบันทึกการติดต่อและกิจกรรม</li>
  <li>ติดตาม Opportunities ที่ใกล้ Expected Close Date</li>
</ol>

<h3>การติดตามและรายงาน</h3>
<ul>
  <li>ดู Pipeline เพื่อเห็นภาพรวม</li>
  <li>กรองตาม Stage, Salesperson, Customer</li>
  <li>ใช้ Expected Revenue สำหรับการคาดการณ์รายได้</li>
  <li>วิเคราะห์ Win/Loss Reasons เพื่อปรับปรุง</li>
</ul>

<h2>การแก้ไขปัญหา</h2>

<h3>Stage ไม่ถูกต้อง</h3>
<ul>
  <li>ตรวจสอบ Pipeline Configuration ใน Admin → Pipeline Studio</li>
  <li>ตรวจสอบว่ามี Stage ถูกต้องในระบบ</li>
</ul>

<h3>Expected Revenue ไม่แสดง</h3>
<ul>
  <li>ตรวจสอบว่ามีการระบุ Amount และ Probability</li>
  <li>ระบบจะคำนวณอัตโนมัติเมื่อมีข้อมูลครบ</li>
</ul>

<h2>เคล็ดลับ</h2>
<ul>
  <li>✅ อัปเดต Stage และ Probability เป็นประจำ</li>
  <li>✅ ระบุ Expected Close Date เพื่อติดตาม</li>
  <li>✅ เพิ่ม Activities เพื่อบันทึกประวัติการเจรจา</li>
  <li>✅ ระบุ Next Step เพื่อไม่ให้พลาดขั้นตอนสำคัญ</li>
  <li>✅ ใช้ Win/Loss Reasons เพื่อเรียนรู้และปรับปรุง</li>
  <li>✅ เชื่อมโยงกับ Customer และ Lead เพื่อติดตามประวัติ</li>
  <li>✅ ใช้ Tags เพื่อจัดกลุ่ม Opportunities</li>
</ul>

<p><em><strong>อัปเดตล่าสุด</strong>: ธันวาคม 2025</em></p>
`;

